import React, { FC, useCallback, useMemo, useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { Stack } from '@mui/material';
import { StringParam, useQueryParams } from 'use-query-params';

import { FolderType } from '@/api/generated';
import BackdropLoader from '@/components/BackdropLoader';
import ErrorAccessDenied from '@/components/ErrorAccessDenied/ErrorAccessDenied';
import ErrorGeneric from '@/components/ErrorGeneric/ErrorGeneric';
import { PermissionGuard } from '@/components/PermissionGuard';
import { PermissionKeys } from '@/hooks/permissions/usePermissions';
import { useSuborganizationPermissions } from '@/hooks/permissions/useSuborganizationPermissions';
import { PROCESS_SETTINGS_STEP_KEYS, toOrganizationSettingsTalentRegistrationForm } from '@/root/linker';
import SettingsTab from '@/views/ProcessSettingsForm/SettingsTab/SettingsTab';

const URL_CONFIG_MAP = {
  locationSlug: StringParam
};

const ProcessSettingsTab: FC = () => {
  const [activeTab, setActiveTab] = useState<string>(PROCESS_SETTINGS_STEP_KEYS.SETTINGS_ACCESS_INVITATION);
  const { organizationSlug } = useParams() as { organizationSlug: string };
  const [{ locationSlug }] = useQueryParams(URL_CONFIG_MAP);

  const suborganizationPermissionVariables = useMemo(
    () => ({
      organizationSlug,
      locationSlug: locationSlug ?? undefined,
      locationType: FolderType.SUB_ORGANIZATION
    }),
    [locationSlug, organizationSlug]
  );

  const {
    value: {
      canUpdateProcessSettingsAccessAndInvitation,
      canUpdateProcessSettingsTestProperties,
      canUpdateProcessSettingsTalentRegistration,
      canUpdateProcessSettingsMessages
    } = {}
  } = useSuborganizationPermissions({
    variables: suborganizationPermissionVariables
  });

  const handleTabChange = useCallback((newTab: string) => {
    setActiveTab(newTab);
  }, []);

  const talentRegistrationFormPath = useMemo(
    () => toOrganizationSettingsTalentRegistrationForm({ organizationSlug }),
    [organizationSlug]
  );

  const navigate = useNavigate();

  const handleTalentRegistrationFormOpen = useCallback(
    () => navigate(talentRegistrationFormPath),
    [navigate, talentRegistrationFormPath]
  );

  if (!locationSlug) {
    return null;
  }

  return (
    <PermissionGuard
      all={[PermissionKeys.SuborganizationcanViewProcessSettings]}
      variables={suborganizationPermissionVariables}
      accessDeniedFallback={<ErrorAccessDenied />}
      errorFallback={<ErrorGeneric />}
      loadingFallback={<BackdropLoader />}
    >
      <Stack
        width="100%"
        p={3}
        sx={{
          '& .MuiFormLabel-root': { textAlign: 'start' },
          '& .MuiFormControl-root': { pb: 0 },
          '& .DropdownSelect-expandMoreIcon': { color: 'text.primary' }
        }}
      >
        <SettingsTab
          activeTab={activeTab}
          onTabChange={handleTabChange}
          isFullScreenMode={false}
          onTalentRegistrationFormOpen={handleTalentRegistrationFormOpen}
          organizationSlug={organizationSlug}
          canUpdateAccessAndInvitation={canUpdateProcessSettingsAccessAndInvitation}
          canUpdateTestProperties={canUpdateProcessSettingsTestProperties}
          canUpdateTalentRegistration={canUpdateProcessSettingsTalentRegistration}
          canUpdateMessages={canUpdateProcessSettingsMessages}
        />
        <Outlet />
      </Stack>
    </PermissionGuard>
  );
};

export default ProcessSettingsTab;
